import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner } from '../ui/loading';
import { Home, Calendar, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { useHouseOwnerAnalytics } from '../../hooks/useDashboardAnalytics';
import { CourseTimeToggle } from '../ui/course-time-toggle';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatShortDate(dateString) {
  const date = new Date(dateString);
  return `${date.getDate()} ${date.toLocaleDateString('en-GB', { month: 'short' })}`;
}

function formatFullDate(dateString) {
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

function GenderBadge({ designation, t }) {
  const isMale = designation === 'M';
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${
      isMale
        ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/30'
        : 'text-pink-600 bg-pink-50 dark:bg-pink-900/30'
    }`}>
      {isMale ? t('dashboard.male') : t('dashboard.female')}
    </span>
  );
}

// ─── Course card (grid view) ──────────────────────────────────────────────────

function HouseOwnerCourseCard({ course, onSelect, isSelected = false, showIndicator = true }) {
  const { t } = useTranslation();

  return (
    <button
      onClick={onSelect}
      className={`group text-left rounded-xl border transition-all w-full overflow-hidden ${
        isSelected
          ? 'border-amber-300 ring-1 ring-amber-300 shadow-md'
          : 'bg-card hover:border-border hover:shadow-md'
      }`}
    >
      {isSelected && (
        <div className="h-1 w-full" style={{ background: 'linear-gradient(135deg, #F6BD66, #F28482, #F5CAC3)' }} />
      )}
      <div className="p-4 sm:p-6">
        {/* Title + dates */}
        <div className="mb-4">
          <div className="font-semibold text-lg leading-tight">{course.name}</div>
          <div className="flex items-center gap-1.5 mt-1 text-sm text-muted-foreground">
            <Calendar className="h-3.5 w-3.5 flex-shrink-0" />
            <span>{formatShortDate(course.start_date)} – {formatShortDate(course.end_date)}</span>
          </div>
        </div>

        {/* Houses */}
        <div className="space-y-2">
          {course.houses.map((house) => (
            <div key={house.uuid} className="flex items-center justify-between gap-2 text-sm">
              <div className="flex items-center gap-2 min-w-0">
                <Home className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                <span className="truncate font-medium">{house.name}</span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {course.show_students ? (
                  <span className="text-muted-foreground">{house.assigned}/{house.capacity}</span>
                ) : (
                  <span className="text-muted-foreground">
                    {house.capacity} {t('houseOwnerDashboard.places')}
                  </span>
                )}
                <GenderBadge designation={house.gender_designation} t={t} />
              </div>
            </div>
          ))}
        </div>

        {showIndicator && (
          <div className="flex items-center justify-end gap-1 mt-4 pt-3 border-t text-muted-foreground/40 group-hover:text-muted-foreground transition-colors">
            <span className="text-xs">{t('dashboard.viewDetails')}</span>
            <ChevronRight className="h-3.5 w-3.5" />
          </div>
        )}
      </div>
    </button>
  );
}

// ─── Pre-cutoff detail (capacity only, no student info) ───────────────────────

function PreCutoffDetail({ course, onBack }) {
  const { t } = useTranslation();

  return (
    <div className="space-y-6 pb-8">
      <div>
        <button
          onClick={onBack}
          className="flex items-center gap-1 py-2 text-sm text-muted-foreground hover:text-foreground mb-2 -ml-1 transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          {t('houseOwnerDashboard.allCourses')}
        </button>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight border-l-4 pl-3" style={{ borderColor: '#F28482' }}>
          {course.name}
        </h1>
        <div className="flex items-center gap-2 mt-1 text-muted-foreground">
          <Calendar className="h-4 w-4" />
          <span>{formatFullDate(course.start_date)} – {formatFullDate(course.end_date)}</span>
        </div>
      </div>

      {/* Info notice */}
      <div className="flex items-start gap-3 rounded-lg bg-muted/50 border p-4 text-sm text-muted-foreground">
        <Clock className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <span>{t('houseOwnerDashboard.studentDetailsNotAvailable')}</span>
      </div>

      {/* House cards — capacity only */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {course.houses.map((house) => (
          <div key={house.uuid} className="rounded-lg border bg-card p-5">
            <div className="flex items-center justify-between gap-2 mb-4">
              <div className="flex items-center gap-2 min-w-0">
                <Home className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <h2 className="font-semibold truncate">{house.name}</h2>
              </div>
              <GenderBadge designation={house.gender_designation} t={t} />
            </div>
            <div className="text-3xl font-bold">{house.capacity}</div>
            <div className="text-sm text-muted-foreground mt-0.5">{t('houseOwnerDashboard.places')}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Post-cutoff detail (students visible) ────────────────────────────────────

function PostCutoffDetail({ course, onBack }) {
  const { t } = useTranslation();

  return (
    <div className="space-y-6 pb-8">
      <div>
        <button
          onClick={onBack}
          className="flex items-center gap-1 py-2 text-sm text-muted-foreground hover:text-foreground mb-2 -ml-1 transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          {t('houseOwnerDashboard.allCourses')}
        </button>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight border-l-4 pl-3" style={{ borderColor: '#F28482' }}>
          {course.name}
        </h1>
        <div className="flex items-center gap-2 mt-1 text-muted-foreground">
          <Calendar className="h-4 w-4" />
          <span>{formatFullDate(course.start_date)} – {formatFullDate(course.end_date)}</span>
        </div>
      </div>

      {course.houses.map((house) => (
        <div key={house.uuid} className="rounded-lg border bg-card p-5">
          {/* House header */}
          <div className="flex items-center justify-between gap-2 mb-4">
            <div className="flex items-center gap-2 min-w-0">
              <Home className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <h2 className="font-semibold text-lg truncate">{house.name}</h2>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <GenderBadge designation={house.gender_designation} t={t} />
              <span className="text-sm text-muted-foreground">
                {house.assigned}/{house.capacity}
              </span>
            </div>
          </div>

          {/* Student list */}
          {(!house.students || house.students.length === 0) ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              {t('houseOwnerDashboard.noStudents')}
            </p>
          ) : (
            <>
              {/* Mobile: card list */}
              <div className="sm:hidden space-y-2">
                {house.students.map((student, idx) => (
                  <div key={student.uuid} className="rounded-md border p-3 text-sm">
                    <div className="font-medium">{idx + 1}. {student.last_name}, {student.first_name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {student.school_year || '—'} · {student.home_county || '—'}
                    </div>
                    <div className="flex flex-col gap-0.5 mt-1.5">
                      {student.parent_phone ? (
                        <a href={`tel:${student.parent_phone}`} className="text-xs text-muted-foreground hover:text-foreground hover:underline">
                          {student.parent_phone}
                        </a>
                      ) : null}
                      {student.parent_email ? (
                        <a href={`mailto:${student.parent_email}`} className="text-xs text-muted-foreground hover:text-foreground hover:underline truncate">
                          {student.parent_email}
                        </a>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop: table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-2 pr-4 text-xs font-medium text-muted-foreground">#</th>
                      <th className="pb-2 pr-4 text-xs font-medium text-muted-foreground">{t('houseOwnerDashboard.name')}</th>
                      <th className="pb-2 pr-4 text-xs font-medium text-muted-foreground">{t('houseOwnerDashboard.year')}</th>
                      <th className="pb-2 pr-4 text-xs font-medium text-muted-foreground">{t('houseOwnerDashboard.county')}</th>
                      <th className="pb-2 pr-4 text-xs font-medium text-muted-foreground">{t('houseOwnerDashboard.parentPhone')}</th>
                      <th className="pb-2 text-xs font-medium text-muted-foreground">{t('houseOwnerDashboard.parentEmail')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {house.students.map((student, idx) => (
                      <tr key={student.uuid} className="border-b border-border/50 last:border-0 hover:bg-muted/30">
                        <td className="py-2.5 pr-4 text-xs text-muted-foreground tabular-nums w-8">{idx + 1}</td>
                        <td className="py-2.5 pr-4 font-medium whitespace-nowrap">
                          {student.last_name}, {student.first_name}
                        </td>
                        <td className="py-2.5 pr-4 text-muted-foreground whitespace-nowrap">{student.school_year || '—'}</td>
                        <td className="py-2.5 pr-4 text-muted-foreground whitespace-nowrap">{student.home_county || '—'}</td>
                        <td className="py-2.5 pr-4 text-muted-foreground whitespace-nowrap">
                          {student.parent_phone ? (
                            <a href={`tel:${student.parent_phone}`} className="hover:text-foreground hover:underline">
                              {student.parent_phone}
                            </a>
                          ) : '—'}
                        </td>
                        <td className="py-2.5 text-muted-foreground">
                          {student.parent_email ? (
                            <a href={`mailto:${student.parent_email}`} className="hover:text-foreground hover:underline">
                              {student.parent_email}
                            </a>
                          ) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Main HouseOwnerDashboard ─────────────────────────────────────────────────

export function HouseOwnerDashboard() {
  const { t } = useTranslation();
  const [selectedCourse, setSelectedCourse] = useState(null);
  const { data, isLoading, error } = useHouseOwnerAnalytics();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-destructive">{t('dashboard.error', 'Error loading dashboard data')}</p>
          <p className="text-sm text-muted-foreground mt-2">{error.message}</p>
        </div>
      </div>
    );
  }

  const { courses } = data || {};
  const selectedCourseData = courses?.find((c) => c.uuid === selectedCourse);
  const otherCourses = courses?.filter((c) => c.uuid !== selectedCourse) || [];

  const n = courses?.length || 0;
  // Pick cols so rows are equal where possible: 1→1, 2→2, 3→3, 4→2 (2×2), 5+→3
  const cols = n === 1 ? 1 : n === 3 ? 3 : n % 2 === 0 ? 2 : 3;
  const gridClass =
    cols === 1 ? 'grid-cols-1 max-w-sm' :
    cols === 2 ? 'grid-cols-1 sm:grid-cols-2 max-w-3xl' :
                 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl';

  return (
    <div className="pb-8">
      {selectedCourse && selectedCourseData ? (
        <div className="flex flex-col md:flex-row gap-5 items-start">
          <div key={selectedCourse} className="flex-1 min-w-0 fade-in">
            {selectedCourseData.show_students ? (
              <PostCutoffDetail course={selectedCourseData} onBack={() => setSelectedCourse(null)} />
            ) : (
              <PreCutoffDetail course={selectedCourseData} onBack={() => setSelectedCourse(null)} />
            )}
          </div>
          {otherCourses.length > 0 && (
            <div className="hidden md:flex md:flex-col w-72 flex-shrink-0 space-y-3 mt-14 fade-in">
              {otherCourses.map((course) => (
                <HouseOwnerCourseCard
                  key={course.uuid}
                  course={course}
                  onSelect={() => setSelectedCourse(course.uuid)}
                  showIndicator={false}
                  isSelected={false}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="fade-in">
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight border-l-4 pl-3" style={{ borderColor: '#F28482' }}>
                {t('houseOwnerDashboard.title')}
              </h1>
              <p className="text-muted-foreground mt-1">
                {t('houseOwnerDashboard.subtitle')}
              </p>
            </div>
            <CourseTimeToggle />
          </div>

          {(!courses || courses.length === 0) ? (
            <div className="flex flex-col items-center justify-center h-64 text-center text-muted-foreground">
              <Home className="h-12 w-12 mb-4 opacity-30" />
              <p className="font-medium">{t('houseOwnerDashboard.noHouses')}</p>
              <p className="text-sm mt-1">{t('houseOwnerDashboard.noHousesDescription')}</p>
            </div>
          ) : (
            <div className={`grid gap-5 mx-auto w-full ${gridClass}`}>
              {courses.map((course) => (
                <HouseOwnerCourseCard
                  key={course.uuid}
                  course={course}
                  onSelect={() => setSelectedCourse(course.uuid)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default HouseOwnerDashboard;
